import { CONFIG } from '../config';
import { OneWayBullet } from '../entities/OneWayBullet';

interface RendererLike {
    getCanvasWidth(): number;
    getCanvasHeight(): number;
    isLandscape: boolean;
}

interface CanvasSize {
    width: number;
    height: number;
    isLandscape: boolean;
}

/**
 * OneWayBullet 엔티티 풀
 */
class OneWayBulletEntityPool {
    private pool: OneWayBullet[] = [];
    private maxPoolSize: number = /iPad|iPhone|iPod/.test(navigator.userAgent) ? 50 : 150;

    get(x: number, y: number, width: number, height: number, vx: number, vy: number, radius: number): OneWayBullet {
        const bullet = this.pool.pop();
        if (bullet) {
            bullet.reset(x, y, width, height, vx, vy, radius);
            return bullet;
        }
        return new OneWayBullet(x, y, width, height, vx, vy, radius);
    }

    release(bullet: OneWayBullet): void {
        if (this.pool.length < this.maxPoolSize) {
            this.pool.push(bullet);
        }
    }

    clear(): void {
        this.pool = [];
    }
}

export class OneWayBulletSpawner {
    private bullets: OneWayBullet[];
    private bulletPool: OneWayBulletEntityPool;
    private timeSinceSpawn: number;
    private renderer: RendererLike | null;
    private bigballMode: boolean;

    constructor() {
        this.bullets = [];
        this.bulletPool = new OneWayBulletEntityPool();
        this.timeSinceSpawn = 0;
        this.renderer = null;
        this.bigballMode = false;
    }

    setBigballMode(enabled: boolean): void {
        this.bigballMode = enabled;
    }

    setRenderer(renderer: RendererLike): void {
        this.renderer = renderer;
    }

    getCanvasSize(): CanvasSize {
        if (this.renderer) {
            return {
                width: this.renderer.getCanvasWidth(),
                height: this.renderer.getCanvasHeight(),
                isLandscape: this.renderer.isLandscape
            };
        }
        return {
            width: CONFIG.CANVAS_WIDTH,
            height: CONFIG.CANVAS_HEIGHT,
            isLandscape: false
        };
    }

    reset(): void {
        // 모든 총알을 풀로 반환
        for (const bullet of this.bullets) {
            this.bulletPool.release(bullet);
        }
        this.bullets = [];
        this.timeSinceSpawn = 0;
    }

    /**
     * 모든 단방향 총알 제거 (폭탄 파워업용)
     */
    clearAll(): void {
        for (const bullet of this.bullets) {
            this.bulletPool.release(bullet);
        }
        this.bullets = [];
    }

    update(deltaTime: number, speedMult: number = 1.0): void {
        this.timeSinceSpawn += deltaTime;

        // Spawn new one-way bullet
        if (this.timeSinceSpawn >= CONFIG.ONE_WAY_BULLET.SPAWN_INTERVAL) {
            this.spawn();
            this.timeSinceSpawn = 0;
        }

        // Get canvas size for boundary checking
        const { width: canvasWidth, height: canvasHeight } = this.getCanvasSize();

        // Update all bullets with swap-and-pop 패턴
        let writeIdx = 0;
        for (let i = 0; i < this.bullets.length; i++) {
            const bullet = this.bullets[i];
            bullet.update(deltaTime * speedMult, canvasWidth, canvasHeight);

            if (bullet.isAlive) {
                this.bullets[writeIdx++] = bullet;
            } else {
                this.bulletPool.release(bullet);
            }
        }
        this.bullets.length = writeIdx;
    }

    spawn(): void {
        const { width: canvasWidth, height: canvasHeight, isLandscape } = this.getCanvasSize();

        // Speed with slight randomness (±10%)
        let speed = CONFIG.ONE_WAY_BULLET.SPEED * (0.9 + Math.random() * 0.2);

        let x: number, y: number, vx: number, vy: number, width: number, height: number;

        // 빅볼 모드일 때 반지름 8배, 속도 2/3
        const radius = this.bigballMode
            ? CONFIG.BULLET.RADIUS * CONFIG.BIGBALL.BULLET_SIZE_MULT
            : CONFIG.BULLET.RADIUS;

        if (this.bigballMode) {
            speed *= CONFIG.BIGBALL.SPEED_MULT;
        }

        if (isLandscape) {
            // Landscape: spawn from right, move left (horizontal lines)
            width = CONFIG.ONE_WAY_BULLET.HEIGHT_MIN +
                Math.random() * (CONFIG.ONE_WAY_BULLET.HEIGHT_MAX - CONFIG.ONE_WAY_BULLET.HEIGHT_MIN);
            height = 1;
            x = canvasWidth + width / 2 + 10;
            y = Math.random() * canvasHeight;
            vx = -speed;
            vy = 0;
        } else {
            // Portrait: spawn from top, move down (vertical lines)
            width = 1;
            height = CONFIG.ONE_WAY_BULLET.HEIGHT_MIN +
                Math.random() * (CONFIG.ONE_WAY_BULLET.HEIGHT_MAX - CONFIG.ONE_WAY_BULLET.HEIGHT_MIN);
            x = Math.random() * canvasWidth;
            y = -height / 2 - 10;
            vx = 0;
            vy = speed;
        }

        this.bullets.push(this.bulletPool.get(x, y, width, height, vx, vy, radius));
    }

    getBullets(): OneWayBullet[] {
        return this.bullets;
    }

    transformPositionsOnOrientationChange(wasLandscape: boolean, isLandscape: boolean): void {
        // Get old and new canvas sizes
        const oldWidth = wasLandscape ? CONFIG.CANVAS_WIDTH_LANDSCAPE : CONFIG.CANVAS_WIDTH;
        const oldHeight = wasLandscape ? CONFIG.CANVAS_HEIGHT_LANDSCAPE : CONFIG.CANVAS_HEIGHT;
        const newWidth = isLandscape ? CONFIG.CANVAS_WIDTH_LANDSCAPE : CONFIG.CANVAS_WIDTH;
        const newHeight = isLandscape ? CONFIG.CANVAS_HEIGHT_LANDSCAPE : CONFIG.CANVAS_HEIGHT;

        for (const bullet of this.bullets) {
            // Normalize position (0-1 range)
            const normalizedX = bullet.x / oldWidth;
            const normalizedY = bullet.y / oldHeight;

            // Rotate 90 degrees based on orientation change direction
            if (wasLandscape && !isLandscape) {
                // Landscape → Portrait
                bullet.x = normalizedY * newWidth;
                bullet.y = (1 - normalizedX) * newHeight;
                // Rotate velocity
                const oldVx = bullet.vx;
                bullet.vx = bullet.vy;
                bullet.vy = -oldVx;
                // Swap width and height for lines
                const oldBulletWidth = bullet.width;
                bullet.width = bullet.height;
                bullet.height = oldBulletWidth;
            } else {
                // Portrait → Landscape
                bullet.x = (1 - normalizedY) * newWidth;
                bullet.y = normalizedX * newHeight;
                // Rotate velocity
                const oldVx = bullet.vx;
                bullet.vx = -bullet.vy;
                bullet.vy = oldVx;
                // Swap width and height for lines
                const oldBulletWidth = bullet.width;
                bullet.width = bullet.height;
                bullet.height = oldBulletWidth;
            }
        }
    }

    render(ctx: CanvasRenderingContext2D): void {
        for (const bullet of this.bullets) {
            bullet.render(ctx);
        }
    }
}
